import { TokenAmount } from "@/components/brand/TokenAmount";
import {
	PortfolioVaultCard,
	type PortfolioVault,
} from "@/components/portfolio/PortfolioVaultCard";
import { useAuth } from "@/lib/auth";
import { useUserVaults } from "@/hooks/useUserVaults";
import { ROUTES } from "@/lib/routes";
import { formatNum } from "@/lib/utils";
import { Coins } from "lucide-react";
import { Link } from "react-router-dom";
import { useMemo } from "react";

export default function PortfolioPage() {
	const { isAuthenticated, balances, isLoggingIn, login } = useAuth();
	const { data: uiVaults = [], isLoading, isError, error } = useUserVaults();

	const vaults: PortfolioVault[] = useMemo(
		() =>
			uiVaults.map((v) => {
				const first =
					v.raw?.collateral?.[0]?.[0] === "ckBTC" ||
					v.raw?.collateral?.[0]?.[0] === "ckETH" ||
					v.raw?.collateral?.[0]?.[0] === "ICP"
						? v.raw.collateral[0][0]
						: "ICP";
				const tokenBal =
					v.raw?.collateral?.find(([k]) => k === first)?.[1] ?? 0;
				return {
					id: v.id,
					collateral: first,
					collateralTokenBal: tokenBal,
					collateralUsd: v.collateralUsd,
					debtUsdm: v.debtUsdm,
					ltv: v.ltvPct / 100,
					healthFactor: v.healthFactor,
					status:
						v.status === "AtRisk" ? ("AtRisk" as const) : ("Active" as const),
				};
			}),
		[uiVaults],
	);

	const summary = useMemo(() => {
		const locked = vaults.reduce((s, v) => s + v.collateralUsd, 0);
		const minted = vaults.reduce((s, v) => s + v.debtUsdm, 0);
		const atRisk = vaults.filter(
			(v) => v.status === "AtRisk" || v.ltv > 0.85,
		).length;
		const avgHf =
			vaults.length === 0
				? 0
				: vaults.reduce(
						(s, v) =>
							s + (Number.isFinite(v.healthFactor) ? v.healthFactor : 2),
						0,
					) / vaults.length;
		return { locked, minted, atRisk, avgHf, count: vaults.length };
	}, [vaults]);

	return (
		<div className="container mx-auto space-y-6 px-4 py-8">
			<div className="flex flex-wrap items-end justify-between gap-3">
				<div>
					<p className="section-eyebrow">Cockpit</p>
					<h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
						Portfolio
					</h1>
					<p className="mt-1 max-w-lg text-sm text-muted-foreground">
						Your live vaults and balances
						{isAuthenticated ? " · on-chain" : " · connect to load"}.
					</p>
				</div>
				<div className="flex gap-2">
					<Link
						to={ROUTES.borrow}
						className="inline-flex items-center gap-2 rounded-xl bg-brand-gradient px-4 py-2 text-sm font-semibold text-primary-foreground shadow-neon"
					>
						<Coins className="size-4" />
						Borrow
					</Link>
					{!isAuthenticated && (
						<button
							type="button"
							disabled={isLoggingIn}
							onClick={() => void login()}
							className="rounded-xl border border-border/60 px-4 py-2 text-sm font-semibold"
						>
							{isLoggingIn ? "…" : "Connect"}
						</button>
					)}
				</div>
			</div>

			{isError ? (
				<p className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm">
					{error instanceof Error ? error.message : "Failed to load vaults"}
				</p>
			) : null}

			<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
				{(
					[
						["Locked USD", summary.locked, true],
						["Minted USDM", summary.minted, false],
						["At risk", summary.atRisk, false],
						["Avg HF", summary.avgHf, false],
					] as const
				).map(([label, val, usd]) => (
					<div key={label} className="card-surface p-4">
						<p className="text-[10px] uppercase text-muted-foreground">{label}</p>
						<p className="mt-1 font-mono-amount text-xl font-bold">
							{usd ? `$${formatNum(val, 0)}` : formatNum(val, label === "Avg HF" ? 2 : 0)}
							{label === "Avg HF" ? "×" : ""}
						</p>
					</div>
				))}
			</div>

			{isAuthenticated ? (
				<div className="flex flex-wrap gap-3 text-sm">
					<span className="rounded-full border border-border px-3 py-1">
						USDM <TokenAmount id="USDM" value={balances.USDM} decimals={2} size="sm" />
					</span>
					<span className="rounded-full border border-border px-3 py-1">
						MMK <TokenAmount id="MMK" value={balances.MMK} decimals={0} size="sm" />
					</span>
				</div>
			) : null}

			{isLoading ? (
				<div className="grid gap-3 sm:grid-cols-2">
					{Array.from({ length: 2 }).map((_, i) => (
						<div key={i} className="skeleton-shimmer h-36" />
					))}
				</div>
			) : vaults.length === 0 ? (
				<div className="card-surface p-8 text-center text-sm text-muted-foreground">
					{isAuthenticated
						? "No vaults yet — open Borrow to create one."
						: "Connect to load your vaults."}
					<div className="mt-4">
						<Link to={ROUTES.borrow} className="font-semibold text-primary">
							Go to Borrow →
						</Link>
					</div>
				</div>
			) : (
				<ul className="grid gap-3 sm:grid-cols-2">
					{vaults.map((v) => (
						<li key={v.id}>
							<PortfolioVaultCard vault={v} />
						</li>
					))}
				</ul>
			)}
		</div>
	);
}
